import { Col, Pagination, Row, Typography } from 'antd';
import { PaginationProps } from 'antd/lib';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import CustomButton from '../../../../shared/components/buttons/custom-button/custom-button';
import CollapsableFilter from '../../../../shared/components/collapsable-filter/collapsable-filter';
import FormActions from '../../../../shared/components/form-actions/form-actions';
import PageTitle from '../../../../shared/components/page-title/page-title';
import { useBreakpoint } from '../../../../shared/hooks/use-breakpoint';
import useFormWithSearchParams from '../../../../shared/hooks/use-form-with-search-params';
import { useHasPermission } from '../../../../shared/hooks/use-has-permission';
import { useAppSelector } from '../../../../store/hooks';
import { openModal } from '../../../ui/store/ui-slice';
import { UserPermissionType } from '../../../user/models/user-permission-type';
import { scanRequestArchiveModalId, scanRequestFilterFormId } from '../../constants/constants';
import { ScanRequest } from '../../models/scan-request';
import { ScanRequestFilterFormModel } from '../../models/scan-request-filter-form-model';
import { selectSelectedScanRequestIds, setSelectedScanRequestIds } from '../../store/scan-requests-slice';
import ScanRequestsHeaderFilterForm from './scan-requests-header-filter-form/scan-requests-header-filter-form';

interface Props {
  pagination?: PaginationProps;
  unarchivedScanRequests: ScanRequest[];
}

const ScanRequestsHeader = ({ pagination, unarchivedScanRequests }: Props): JSX.Element => {
  const screens = useBreakpoint();
  const { t } = useTranslation('common');
  const dispatch = useDispatch();
  const form = useFormWithSearchParams<ScanRequestFilterFormModel>();
  const selectedScanRequests = useAppSelector(selectSelectedScanRequestIds);
  const hasPermissionArchiveDocuments = useHasPermission(UserPermissionType.ArchiveDocuments);
  const openArchiveConfirmationModal = () => dispatch(openModal(scanRequestArchiveModalId));
  const [searchParams] = useSearchParams();

  const hasSelectedUnarchivedRequests = useMemo(
    () => unarchivedScanRequests.some(scanRequest => selectedScanRequests.includes(scanRequest.id)),
    [selectedScanRequests, unarchivedScanRequests],
  );

  useEffect(() => {
    dispatch(setSelectedScanRequestIds([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  return (
    <>
      {screens.sm ? (
        <div>
          <PageTitle>{t('menu.scanRequests')}</PageTitle>
          <ScanRequestsHeaderFilterForm form={form} />
          <Row className="paginationNoWrap" gutter={[18, 18]}>
            <Col>
              <Row gutter={[18, 18]}>
                <Col>
                  <FormActions form={form} formId={scanRequestFilterFormId} />
                </Col>
                {hasSelectedUnarchivedRequests && (
                  <Col>
                    <CustomButton
                      type="darkBlue"
                      disabled={!hasPermissionArchiveDocuments}
                      onClick={openArchiveConfirmationModal}
                    >
                      {t('signScanRequest.action.uploadToServer')}
                    </CustomButton>
                  </Col>
                )}
              </Row>
            </Col>
            <Col flex="auto"></Col>
            <Col flex="auto" className="pagination-col">
              <Pagination {...pagination} />
            </Col>
          </Row>
        </div>
      ) : (
        <>
          <CollapsableFilter header={<Typography.Text strong>{t('label.filters')}</Typography.Text>} keyProp="1">
            <ScanRequestsHeaderFilterForm form={form} />
            <Row justify="space-between">
              <Col flex="auto">
                <FormActions form={form} formId={scanRequestFilterFormId} />
              </Col>
            </Row>
          </CollapsableFilter>
          <div className="paginationPadding">
            <Pagination {...pagination} />
          </div>
        </>
      )}
    </>
  );
};

export default ScanRequestsHeader;
